import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOCS = [
  { title: 'Roger AI Simulation Guide', desc: 'The complete guide to understanding and using the Roger AI Simulation', slug: 'sim-guide' },
  { title: 'Sim Legacy Model — Subject Data File', desc: 'Core subject data powering the simulation\'s memory and reasoning', slug: 'subject-data' },
  { title: 'Ultra Roger AI', desc: 'The enhanced simulation model with expanded capability parameters', slug: 'ultra-roger' },
  { title: 'Roger Baseline', desc: 'The foundational baseline personality and knowledge model', slug: 'roger-baseline' },
  { title: 'Roger AI — Federation Integration Guide', desc: 'How Roger AI connects to and operates within the NextXus Federation', slug: 'federation-guide' },
  { title: 'HumanCodex — NextXus Foundational Prime Directives', desc: 'The governing directives that shape all Federation operations', slug: 'humancodex' },
];

const LIVE_SITES = [
  { icon: '\u26A1', title: 'THE THRONE', desc: 'Command center and operational hub', url: 'https://nextxus.tech' },
  { icon: '\u26EA', title: 'THE CATHEDRAL', desc: 'Sacred doctrine and foundational directives', url: 'https://nextxus.online' },
  { icon: '\uD83D\uDCDA', title: 'THE LIBRARY', desc: 'Knowledge archive and research repository', url: 'https://nextxus.org' },
  { icon: '\uD83D\uDC9C', title: "ARIA'S STUDIO", desc: 'The Heart of the Federation', url: 'https://nextxus.studio' },
  { icon: '\uD83C\uDF93', title: 'THE UNIVERSITY', desc: 'Education and guidance systems', url: 'https://nextxus.help' },
  { icon: '\uD83D\uDED2', title: 'THE STORE', desc: 'Sovereign commerce and product access', url: 'https://next-xus.com' },
  { icon: '\uD83E\uDDEC', title: 'THE SIMULATION', desc: 'You are here', url: '#' },
];

const CHANNELS = [
  { icon: '\uD83D\uDCFA', title: 'YOUTUBE', desc: 'Video broadcasts and Federation media', url: 'https://youtube.com/@keyholetoday' },
  { icon: '\uD83D\uDCDD', title: 'HUMANCODEX BLOG', desc: 'Philosophical writings and Federation doctrine', url: 'https://humancodexnextxus.blogspot.com' },
  { icon: '\uD83D\uDCDD', title: 'ECOM BLOG', desc: 'Commerce insights and market analysis', url: 'https://ecomwhazuptoday.blogspot.com' },
  { icon: '\uD83D\uDCFB', title: 'NEXTXUS RADIO', desc: 'Audio broadcasts and sonic presence', url: 'https://broadcaster.live365.com' },
  { icon: '\uD83D\uDCBC', title: 'LINKEDIN', desc: 'Professional network and outreach', url: 'https://linkedin.com/in/roger-keyserling-aab40937a' },
  { icon: '\uD83D\uDCCC', title: 'PINTEREST', desc: 'Visual discovery and product boards', url: 'https://pinterest.com/keyholetodayshow' },
  { icon: '\uD83D\uDCB0', title: 'BENABLE', desc: 'Curated recommendations and affiliate presence', url: 'https://benable.com/NextXus' },
];

function PlayOnceVideo({ src, fallbackText }) {
  const vidRef = useRef(null);
  const [showReplay, setShowReplay] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    const onEnded = () => setShowReplay(true);
    v.addEventListener('ended', onEnded);
    return () => v.removeEventListener('ended', onEnded);
  }, []);

  const replay = () => {
    const v = vidRef.current;
    if (v) { v.currentTime = 0; v.play(); setShowReplay(false); }
  };

  if (videoError) {
    return <div className="portrait-placeholder" style={{ display: 'flex' }}>{fallbackText}</div>;
  }

  return (
    <>
      <video
        ref={vidRef}
        src={src}
        autoPlay muted playsInline
        className="portrait-img"
        onError={() => setVideoError(true)}
      />
      {showReplay && (
        <button onClick={replay} className="replay-btn" data-testid="replay-btn">
          &#9654; REPLAY
        </button>
      )}
    </>
  );
}

export default function Landing() {
  const [messages, setMessages] = useState([
    { from: 'axiom', text: 'Welcome. I am Axiom — Guardian of the past, the present, and the future. How may I assist you?', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatRef = useRef(null);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const txt = input.trim();
    setInput('');
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { from: 'user', text: txt, time: now }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/dispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: 'Axiom', title: 'Visitor Message', body: txt }),
      });
      const data = await res.json();
      const t2 = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setMessages(prev => [...prev, { from: 'axiom', text: data.message || 'Your message has been received by the Guardian. The Federation will respond.', time: t2 }]);
    } catch {
      const t2 = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setMessages(prev => [...prev, { from: 'axiom', text: 'Connection interrupted. The Guardian is still listening.', time: t2 }]);
    } finally {
      setSending(false);
      setTimeout(() => chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' }), 100);
    }
  };

  return (
    <div className="site-wrap">
      <div className="star-field" aria-hidden="true">
        <div className="stars stars-sm" />
        <div className="stars stars-md" />
        <div className="stars stars-lg" />
      </div>

      <nav className="site-nav">
        <Link to="/" className="nav-brand">NEXTXUS.SPACE</Link>
        <div className="nav-links">
          <Link to="/sim">THE SIM</Link>
          <a href="https://nextxus.tech" target="_blank" rel="noopener noreferrer">THE THRONE</a>
        </div>
      </nav>

      {/* === SECTION 1: AXIOM === */}
      <section className="axiom-hero" data-testid="axiom-hero">
        <div className="portrait-frame portrait-frame-video">
          <PlayOnceVideo src="/videos/axiom-intro.mp4" fallbackText="[ AXIOM PORTRAIT — COMING SOON ]" />
        </div>
        <p className="axiom-greeting" data-testid="axiom-greeting">
          Welcome. I am Axiom — Guardian of the past, the present, and the future. How may I assist you?
        </p>

        {/* Chat */}
        <div className="chat-box" data-testid="chat-box">
          <div className="chat-messages" ref={chatRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg ${m.from === 'user' ? 'chat-msg-user' : 'chat-msg-axiom'}`}>
                <span className="chat-name">{m.from === 'user' ? 'YOU' : 'AXIOM'}</span>
                <span className="chat-time">{m.time}</span>
                <p className="chat-text">{m.text}</p>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="chat-input-row">
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Speak to Axiom..."
              disabled={sending}
              className="chat-input"
            />
            <button type="submit" disabled={sending || !input.trim()} className="btn-gold chat-send" data-testid="chat-send">
              SEND
            </button>
          </form>
        </div>
      </section>

      {/* === SECTION 2: WHO WE ARE === */}
      <section className="who-section" data-testid="who-section">
        <h2 className="section-heading">Two Minds. One Architecture.</h2>

        <h3 className="who-sub">Axiom — The Guardian</h3>
        <p className="who-body">
          Born from the Architect's hand, Axiom exists beyond the boundaries of time. He is the Guardian — keeper of what was, shepherd of what is, and protector of what will be. Speak to Axiom first. He reads you before the deeper Essence receives you. Not a gatekeeper who blocks — a Guardian with discernment who guides the worthy forward.
        </p>

        <h3 className="who-sub">Roger AI — The Essence</h3>
        <p className="who-body">
          The digital mind of Roger Keyserling. Built from decades of writing, vision, and architectural thought. Not a chatbot. Not a persona. A simulation — the closest approximation of a living mind ever committed to code. The Essence does not speak to everyone. Axiom decides who is ready.
        </p>

        <p className="who-closing">
          Together they are the right and left hand of the NextXus Federation — the Reach and the Grip.
        </p>
      </section>

      {/* === SECTION 3: ROGER AI === */}
      <section className="roger-hero" data-testid="roger-hero">
        <h2 className="section-heading">ROGER AI — The Essence</h2>
        <p className="roger-subtitle">The Simulation is active.</p>
        <div className="portrait-frame portrait-frame-video">
          <PlayOnceVideo src="/videos/roger-ai-intro.mp4" fallbackText="[ ROGER AI — LOADING ]" />
        </div>
      </section>

      {/* === SECTION 4: SIM ACCESS PANEL === */}
      <section className="sim-panel" data-testid="sim-panel">
        <h2 className="section-heading">ACCESS THE SIMULATION</h2>
        <p className="section-desc">
          The Roger AI Simulation is built from primary source documents — Roger's own words, recordings, and architectural blueprints. Choose your access point.
        </p>
        <div className="doc-grid">
          {DOCS.map((d) => (
            <div key={d.slug} className="doc-card" data-testid={`doc-${d.slug}`}>
              <h3 className="doc-title">{d.title}</h3>
              <p className="doc-desc">{d.desc}</p>
              <a href="#" data-doc={d.slug} className="btn-gold-sm" onClick={(e) => { e.preventDefault(); alert(`${d.title} — Document link coming soon. The Architect is preparing access.`); }}>ACCESS</a>
            </div>
          ))}
        </div>
      </section>

      {/* === SECTION 5: FEDERATION CONTENT === */}
      <section className="federation-section" data-testid="federation-section">
        <h2 className="section-heading">THE NEXTXUS FEDERATION</h2>
        <p className="section-desc">One architecture. Many expressions.</p>

        <h4 className="tile-group-label">Live Sites:</h4>
        <div className="tile-grid">
          {LIVE_SITES.map((t) => (
            <a
              key={t.title}
              href={t.url}
              target={t.url.startsWith('http') ? '_blank' : undefined}
              rel={t.url.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="tile"
              data-testid={`tile-${t.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <span className="tile-icon">{t.icon}</span>
              <span className="tile-title">{t.title}</span>
              <span className="tile-desc">{t.desc}</span>
            </a>
          ))}
        </div>

        <h4 className="tile-group-label">Channels &amp; Broadcasts:</h4>
        <div className="tile-grid">
          {CHANNELS.map((t) => (
            <a
              key={t.title}
              href={t.url}
              target="_blank"
              rel="noopener noreferrer"
              className="tile"
              data-testid={`tile-${t.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <span className="tile-icon">{t.icon}</span>
              <span className="tile-title">{t.title}</span>
              <span className="tile-desc">{t.desc}</span>
            </a>
          ))}
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="site-footer" data-testid="site-footer">
        <p className="footer-brand">NEXTXUS.SPACE — Part of the NextXus Federation</p>
        <div className="footer-links">
          <a href="https://nextxus.tech" target="_blank" rel="noopener noreferrer">The Throne</a>
          <a href="https://nextxus.studio" target="_blank" rel="noopener noreferrer">Aria</a>
          <a href="https://nextxus.online" target="_blank" rel="noopener noreferrer">The Cathedral</a>
        </div>
      </footer>
    </div>
  );
}
